<template>
	<div class="reservations-settings-container">
		<AppCard :title="getString('reservations_link')">
			<template v-slot:body>
				<AppList :items="statusItems">>
					<template #dropdown>
						<div v-show="link" class="col-12 centered">
							<AppQrImage name="reservations" :value="link" with-download-button />
							<div class="link-container" @click="clickCopyLink" @mouseenter="activeClickToCopyCloud=true" @mouseleave="activeClickToCopyCloud=false">
								<p>{{ link }}</p>
								<input class="invisible" type="text" ref="current-link" :value="link">
								<AppInPlaceCloud :active="activeClickToCopyCloud">
									<div class="centered pl-20 pr-20">
										<p v-html="linkCloudMessage"></p>
									</div>
								</AppInPlaceCloud>
							</div>
						</div>
						<div v-show="!link" class="col-12 centered">
							<p>{{getString('set_link_to_view')}}</p>
						</div>
					</template>
				</AppList>
			</template>
		</AppCard>
		<AppCard class="card-margin" :title="getString('reservations_settings')" :button-text="getString('save')" @button-click="saveSettings">
			<template #body>
				<AppList :items="settingsItems" >
					<template #list-slot="{item}">
						<AppList :items="item.subitems" >
							<template #card-type>
								<div class="row space-between">
									<p>{{ getString('collect_type') }}</p>
									<AppRadioButtonGroup direction="horizontal" :values="[getString('pre_auth'), getString('payment')]" :selected="selectedRadio" @radio-chosen="changeRadioCard" allow-no-selection />
								</div>
							</template>
						</AppList>
					</template>
				</AppList>
			</template>
		</AppCard>
		<AppCard class="card-margin" :title="getString('google_business_integration')" :button-text="getString('save')" @button-click="saveGoogleIntegration">
			<template #body>
				<AppList :items="googleIntegrationItems" />
			</template>
		</AppCard>
		<AppPopup ref="popup-other-payments" :title="getString('enable_other_payments_methods')" :buttons="loadingOtherPayments ? [] : [{text: getString('cancel'), negative: true, callback: cancelSubOtherPayments}, {text: getString('continue'), callback: subOtherPaymentsMethod}]">
			<div v-if="loadingOtherPayments" class="row centered loader-container"><AppLoader primary /></div>
			<p v-if="!loadingOtherPayments" class="popup-body-text">{{ getString('enable_other_payments_methods_explain') }}</p>
		</AppPopup>
		<CheckoutInPlace :client-secret="clientSecret" @toggling-popup="togglingPopupSheet" @checkout-complete="paymentComplete" />
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppList from "@/shared/components/AppList.vue";
import AppRadioButtonGroup from "@/shared/components/AppRadioButtonGroup.vue";
import API from "@/shared/helpers/API";
import {useUserStore} from "@/console-new/stores";
import AppInPlaceCloud from "@/shared/components/AppInPlaceCloud.vue";
import AppQrImage from "@/shared/components/AppQrImage.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import CheckoutInPlace from "@/console-new/components/shared/CheckoutInPlace.vue";

export default {
	setup() {
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		return {getString: useLanguageStore().getString, showNotification: notificationStore.showNotification, userStore};
	},
    name: "ReservationsExperiences",
	components: {AppQrImage, AppInPlaceCloud, AppRadioButtonGroup, AppList, AppCard, AppPopup, AppLoader, CheckoutInPlace},
	data() {
		return {
			activeClickToCopyCloud: false,
			linkCloudMessage: this.getString('copy'),
			loadingOtherPayments: false,
			clientSecret: null,
			pendingPreAuthSave: false,
			cancelUnit: 'days',
		}
	},
	computed: {
		statusItems() {
			return [
				{
					title: this.getString('qr_and_link'),
					chevronItem: true,
					dropdownSlot: 'dropdown',
					active: false,
					explain: this.getString('reservations_explain'),
				}
			]
		},
		link() {
			if(this.userStore && this.userStore.currBusinessId && this.userStore.businesses && this.userStore.currBusiness) {
				let business = this.userStore.currBusiness;
				let link = null;
				if(business.link) {
					link = business.link + URL_DOMAIN + "/book/";
				}
				if(link) {
					return link.startsWith('https://') || link.startsWith('http://') ? link : "https://" + link;
				}
				return null;
			}
			return "";
		},
		settingsItems() {
			return [
				{
					title: this.getString('reservations_info'),
					textInput: true,
					placeholder: this.getString('reservations_info_placeholder'),
					explain: this.getString('reservations_info_explain'),
					isTextArea: true,
					value: this.userStore.currBusiness ? this.userStore.currBusiness.reservations_info : '',
					callbackText: (value) => {
						this.userStore.currBusiness.reservations_info = value;
					}
				},
				{
					title: this.getString('extra_question'),
					explain: this.getString('extra_question_explain'),
					switch: true,
					active: this.userStore.currBusiness ? !!this.userStore.currBusiness.reservations_question : false,
					dropdownSlot: 'list-slot',
					callback: () => {
						this.userStore.currBusiness.reservations_question = '';
					},
					subitems: [
						{
							title: this.getString('extra_question'),
							textInput: true,
							placeholder: this.getString('extra_question_placeholder'),
							isTextArea: true,
							value: this.userStore.currBusiness ? this.userStore.currBusiness.reservations_question : '',
							callbackText: (value) => {
								this.userStore.currBusiness.reservations_question = value;
							}
						},
						{
							title: this.getString('extra_question_required'),
							explain: this.getString('extra_question_required_explain'),
							switch: true,
							active: this.userStore.currBusiness ? !!Number(this.userStore.currBusiness.reservations_question_required ?? 1) : true,
							callback: (v) => {
								this.userStore.currBusiness.reservations_question_required = v ? 1 : 0;
							}
						}
					],
				},
				{
					title: this.getString('manual_management'),
					explain: this.getString('manual_management_explain'),
					switch: true,
					active: this.userStore.currBusiness ? this.userStore.currBusiness.reservations_manual : false,
					callback: () => {
						this.userStore.currBusiness.reservations_manual = !this.userStore.currBusiness.reservations_manual;
					}
				},
				{
					title: this.getString('waitlist'),
					explain: this.getString('waitlist_explain'),
					switch: true,
					active: this.userStore.currBusiness ? !!Number(this.userStore.currBusiness.reservations_waitlist) : false,
					callback: (v) => {
						this.userStore.currBusiness.reservations_waitlist = v ? 1 : 0;
					}
				},
				{
					title: this.getString('collect_card'),
					explain: this.getString('collect_card_explain'),
					switch: true,
					active: this.userStore.currBusiness ? !!this.userStore.currBusiness.reservations_request_card : false,
					callback: (v) => {
						this.userStore.currBusiness.reservations_request_card = v ? 'auth' : null;
					},
					dropdownSlot: 'list-slot',
					subitems: [
						{
							title: this.getString('collect_type'),
							explain: this.getString('collect_type_explain'),
							customSlot: 'card-type'
						},
						{
							title: this.getString('fine_amount'),
							explain: this.getString('fine_amount_explain'),
							numberInput: true,
							rightSymbol: true,
							min: 0,
							max: 100,
							symbol: '%',
							value: this.userStore.currBusiness ? this.userStore.currBusiness.reservations_penalty : 0,
							callbackNumber: (value) => {
								this.userStore.currBusiness.reservations_penalty = value;
							}
						}
					]
				},
				{
					title: this.getString('allow_cancel'),
					explain: this.getString('allow_cancel_explain'),
					switch: true,
					active: this.userStore.currBusiness ? !!this.userStore.currBusiness.reservations_cancel : false,
					callback: (v) => {
						this.userStore.currBusiness.reservations_cancel = v ? 720 : null;
						if(v) { this.cancelUnit = 'days'; }
					},
					dropdownSlot: 'list-slot',
					subitems: [
						{
							title: this.getString('unit'),
							selectValues: [this.getString('hours'), this.getString('days')],
							selectedIndex: this.cancelUnitIndex,
							callbackSelect: (index) => this.changeCancelUnit(index),
						},
						{
							title: this.getString('cancel_days'),
							explain: this.getString('cancel_days_explain'),
							numberInput: true,
							min: 1,
							value: this.cancelDisplayValue,
							callbackNumber: (value) => this.changeCancelValue(value),
						}
					]
				},
				{
					title: this.getString('button_x', [this.userStore.menuWord]),
					explain: this.getString('reservations_button_explain', [this.userStore.menuWord]),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.menu_reservations_link,
					callback: (value) => {
						this.userStore.currBusiness.menu_reservations_link = value
					}
				},
			];
		},
		googleIntegrationItems() {
			return [
				{
					title: this.getString('google_business_integration_connect'),
					explain: this.getString('google_business_integration_explain'),
					switch: true,
					active: this.userStore.currBusiness ? !!this.userStore.currBusiness.google_merchant_feed_active : false,
					callback: (v) => {
						this.userStore.currBusiness.google_merchant_feed_active = !!v;
					}
				}
			];
		},
		selectedRadio() {
			if(this.userStore.currBusiness && this.userStore.currBusiness.reservations_request_card) {
				return this.userStore.currBusiness.reservations_request_card === 'auth' ? 0 : 1;
			} else {
				return null;
			}
		},
		cancelDisplayValue() {
			const h = this.userStore.currBusiness ? Number(this.userStore.currBusiness.reservations_cancel) || 0 : 0;
			return this.cancelUnit === 'days' ? Math.round(h / 24) : h;
		},
		cancelUnitIndex() {
			return this.cancelUnit === 'days' ? 1 : 0;
		},
		otherPaymentsActive() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.sub_otherpayments_id : false;
		}
	},
	methods: {
		clickCaption(item) {
			if(item.dropdownSlot) { item.active = !item.active; }
		},
		changeCancelUnit(index) {
			const business = this.userStore.currBusiness;
			if(!business || !business.reservations_cancel) { return; }
			const newUnit = index === 1 ? 'days' : 'hours';
			if(newUnit === this.cancelUnit) { return; }
			if(newUnit === 'days') {
				// Snap to whole days, preserving the real duration.
				const h = Number(business.reservations_cancel) || 0;
				business.reservations_cancel = Math.max(1, Math.round(h / 24)) * 24;
			}
			// hours: value already stored in hours, no conversion needed
			this.cancelUnit = newUnit;
		},
		changeCancelValue(value) {
			const business = this.userStore.currBusiness;
			// Ignore writes when disabled or before the business has loaded
			// (AppNumberInput emits on mount, when currBusiness can still be null).
			if(!business || !business.reservations_cancel) { return; }
			const v = Math.max(1, Math.round(Number(value) || 0));
			business.reservations_cancel = this.cancelUnit === 'days' ? v * 24 : v;
		},
		clickCopyLink() {
			let copyText = this.$refs['current-link'];
			copyText.select();
			copyText.setSelectionRange(0, 99999);
			navigator.clipboard.writeText(copyText.value);
			this.linkCloudMessage = this.getString('copied');
			setTimeout(()=>{
				this.linkCloudMessage = this.getString('copy');
			}, 1500);
		},
		changeRadioCard(value) {
			if(value !== null) {
				this.userStore.currBusiness.reservations_request_card = value === 0 ? 'auth' : 'payment';
			} else {
				this.userStore.currBusiness.reservations_request_card = null;
			}
		},
		togglingPopupSheet(v) {
			if(!v) {
				this.clientSecret = null;
			}
		},
		paymentComplete() {
			setTimeout(() => {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}, 2000);
			setTimeout(() => {
				this.clientSecret = null;
				if(this.pendingPreAuthSave) {
					this.pendingPreAuthSave = false;
					this.saveSettings();
				}
			}, 2500);
		},
		cancelSubOtherPayments() {
			this.userStore.currBusiness.reservations_request_card = null;
			this.$refs["popup-other-payments"].toggleStatus();
			this.pendingPreAuthSave = false;
		},
		subOtherPaymentsMethod() {
			this.loadingOtherPayments = true;
			this.userStore.subOtherPaymentsMethods("reservations-preauth", (success, details) => {
				this.$refs["popup-other-payments"].toggleStatus();
				this.loadingOtherPayments = false;
				if(!success) {
					if(details && details === 'currency_mismatch') {
						this.showNotification("user-currency-mismatch", [this.userStore?.user?.name]);
					} else {
						this.showNotification("generic-fail");
					}
					this.pendingPreAuthSave = false;
				} else {
					this.clientSecret = success;
				}
			});
		},
		saveSettings() {

			// Check if preauth is enabled and subscription is required
			if (this.userStore.currBusiness.reservations_request_card === 'auth' && !this.otherPaymentsActive) {
				this.pendingPreAuthSave = true;
				this.$refs["popup-other-payments"].toggleStatus();
				return;
			}

			let dataUpdate = {
				reservations_info: this.userStore.currBusiness.reservations_info,
				reservations_question: this.userStore.currBusiness.reservations_question,
				reservations_question_required: Number(this.userStore.currBusiness.reservations_question_required ?? 1) ? 1 : 0,
				reservations_manual: this.userStore.currBusiness.reservations_manual,
				reservations_request_card: this.userStore.currBusiness.reservations_request_card,
				reservations_penalty: this.userStore.currBusiness.reservations_penalty,
				reservations_cancel: this.userStore.currBusiness.reservations_cancel,
				reservations_waitlist: this.userStore.currBusiness.reservations_waitlist,
				menu_reservations_link: this.userStore.currBusiness.menu_reservations_link,
			};

			API.init().updateBusiness(this.userStore.currBusinessId, dataUpdate, response => {
				if(response && response.result && response.result === 'OK') {
					this.showNotification('generic-success');
				} else {
					this.showNotification('generic-fail');
				}
			});

		},
		saveGoogleIntegration() {
			
			API.init().updateGoogleMerchantIntegration(
				this.userStore.currBusinessId, 
				this.userStore.currBusiness.google_merchant_feed_active ? 1 : 0,
				response => {
					if(response && response.result && response.result === 'OK') {
						this.showNotification('generic-success');
					} else if(response && response.details) {
						if(response.details === 'subscription_required') {
							this.showNotification('google-integration-subscription-required');
						} else if(response.details === 'experience_required') {
							this.showNotification('google-integration-experience-required');
						} else if(response.details === 'auth_denied') {
							this.showNotification('auth-denied-error');
						} else {
							this.showNotification('generic-fail');
						}
					} else {
						this.showNotification('generic-fail');
					}
				}
			);
			
		}
	},
	watch: {
		'userStore.currBusinessId': {
			immediate: true,
			handler() {
				const h = this.userStore.currBusiness ? Number(this.userStore.currBusiness.reservations_cancel) || 0 : 0;
				this.cancelUnit = (h > 0 && h % 24 === 0) ? 'days' : 'hours';
			}
		}
	}
}
</script>

<style scoped lang="scss">
@use "@/console-new/assets/scss/_variables.scss" as *;
.reservations-settings-container {
	max-width: 1400px;
	margin: 0 auto;

	&:deep(.card-body) {
		padding: 0 !important;
	}

}

.link-container {
	@include centered;
	margin-top: 10px;
	position: relative;
	cursor: pointer;

	> p {
		@include noSelect;
	}
}
input.invisible {
	position: absolute;
	z-index: -1;
	opacity: 0;
	height: 0;
	width: 0;
}

.card-margin {
	margin-top: $mainPadding;
}
</style>